import React from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import { Inventory2 as Inventory2Icon } from '@mui/icons-material';
import type { LoteAsignacion } from '../../services/loteService';

export type LoteChipStatus = 'ok' | 'incomplete' | 'expired' | 'none';

export function getLoteChipStatus(
  controla: boolean,
  cantidad: number,
  lotes?: LoteAsignacion[] | null,
): LoteChipStatus {
  if (!controla) return 'none';
  const list = lotes ?? [];
  if (list.length === 0) return 'incomplete';
  const suma = list.reduce((acc, l) => acc + (Number(l.cantidad) || 0), 0);
  if (Math.abs(suma - cantidad) > 0.0005) return 'incomplete';
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const vencido = list.some((l) => {
    if (!l.fechaVencimiento) return false;
    const f = new Date(l.fechaVencimiento);
    f.setHours(0, 0, 0, 0);
    return f < hoy;
  });
  if (vencido) return 'expired';
  return 'ok';
}

interface LoteChipProps {
  controla: boolean;
  cantidad: number;
  lotes?: LoteAsignacion[] | null;
  onClick?: () => void;
  dense?: boolean;
  /** Solo icono (p. ej. grilla POS densa). */
  iconOnly?: boolean;
  /** Consulta: tip habla de ver lotes, no de asignar. */
  readOnly?: boolean;
}

const STATUS_COLOR = {
  ok: 'success.main',
  incomplete: 'warning.main',
  expired: 'error.main',
} as const;

const LoteChip: React.FC<LoteChipProps> = ({
  controla,
  cantidad,
  lotes,
  onClick,
  dense,
  iconOnly,
  readOnly,
}) => {
  const status = getLoteChipStatus(controla, cantidad, lotes);
  if (status === 'none') return null;

  const tip = readOnly
    ? status === 'incomplete'
      ? 'Sin lotes registrados'
      : status === 'expired'
        ? `Lotes entregados (vencidos)${lotes?.length ? ` — ${lotes.length}` : ''}`
        : `Ver lotes entregados${lotes?.length ? ` (${lotes.length})` : ''}`
    : status === 'incomplete'
      ? 'Asignar lote / serie (incompleto)'
      : status === 'expired'
        ? 'Lote vencido — revisar asignación'
        : `Asignar lote / serie${lotes?.length ? ` (${lotes.length})` : ''}`;

  if (iconOnly) {
    const btn = (
      <IconButton
        size="small"
        onClick={onClick}
        disabled={!onClick}
        aria-label={tip}
        sx={{
          p: 0.25,
          color: STATUS_COLOR[status],
          flexShrink: 0,
        }}
        tabIndex={-1}
      >
        <Inventory2Icon sx={{ fontSize: 16 }} />
      </IconButton>
    );
    return (
      <Tooltip title={tip}>
        {onClick ? btn : <span style={{ display: 'inline-flex' }}>{btn}</span>}
      </Tooltip>
    );
  }

  const map = dense
    ? {
        ok: { label: `Lotes (${lotes?.length ?? 0})`, color: 'success' as const },
        incomplete: { label: 'Lotes', color: 'warning' as const },
        expired: { label: 'Vencido', color: 'error' as const },
      }
    : {
        ok: { label: `Lotes (${lotes?.length ?? 0})`, color: 'success' as const },
        incomplete: { label: 'Lotes incompletos', color: 'warning' as const },
        expired: { label: 'Lote vencido', color: 'error' as const },
      };
  const cfg = map[status];

  return (
    <Tooltip title={tip}>
      <Chip
        size="small"
        label={cfg.label}
        color={cfg.color}
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          height: dense ? 22 : undefined,
          maxWidth: dense ? 88 : undefined,
          flexShrink: 0,
          '& .MuiChip-label': dense
            ? { px: 0.75, fontSize: '0.7rem', whiteSpace: 'nowrap' }
            : undefined,
        }}
      />
    </Tooltip>
  );
};

export default LoteChip;
